import { useQuery } from '@tanstack/react-query';

import { queryKeys } from '~/api/queryKeys';
import { fetchNavigation } from '~/api/services/navigation.service';
import { getSubsidiaryScreens } from '~/features/organization/services/screen-access.service';
import type { TenantScreen } from '~/features/organization/types/screen.types';
import { useAuthStore } from '~/store/auth.store';
import type { NavGroupApi, NavItemApi, NavigationResponse } from '~/types/navigation.types';

const ONE_MINUTE = 60 * 1000;
const FIVE_MINUTES = 5 * 60 * 1000;

/** Effective sort order of a screen row — the subsidiary override wins, as on the backend. */
function effectiveOrder(screen: TenantScreen): number {
  return screen.customSortOrder ?? screen.sortOrder;
}

/** Maps a subsidiary screen row onto a sidebar item, honouring its custom title/icon/order. */
function toNavItem(screen: TenantScreen): NavItemApi {
  return {
    id: screen.id,
    title: screen.customTitle ?? screen.name,
    code: screen.code,
    path: screen.route,
    icon: screen.customIcon ?? screen.icon,
    badge: null,
    badgeColor: null,
    scope: '',
    sortOrder: effectiveOrder(screen),
    items: [],
  };
}

/**
 * Adds back the screens `/screens/navigation` dropped only because the subsidiary switched them
 * off on Navigation Access — super admins override subsidiary rules and see every screen. The
 * subsidiary list already applies the backend's scope rule (no SYSTEM_ADMIN screens outside the
 * system subsidiary), so nothing hidden by scope comes back. A disabled parent returns with all
 * its active children; a disabled child joins its parent group when that group is present.
 */
function withSubsidiaryDisabledScreens(
  nav: NavigationResponse,
  screens: TenantScreen[],
): NavigationResponse {
  const active = screens.filter((s) => s.isActive && s.route);
  const disabled = active.filter((s) => !s.isEnabledForSubsidiary);
  if (disabled.length === 0) return nav;

  const known = new Set<string>();
  for (const group of nav.groups) {
    known.add(group.path);
    for (const item of group.items) known.add(item.path);
  }
  const bySort = (a: NavItemApi, b: NavItemApi) => a.sortOrder - b.sortOrder;

  // Existing groups gain their disabled children.
  const groups: NavGroupApi[] = nav.groups.map((group) => {
    const extra = disabled
      .filter((s) => s.parentId === group.id && !known.has(s.route))
      .map(toNavItem);
    return extra.length ? { ...group, items: [...group.items, ...extra].sort(bySort) } : group;
  });

  // Disabled top-level screens return as groups, with every active child of theirs.
  for (const screen of disabled) {
    if (screen.parentId !== null || known.has(screen.route)) continue;
    groups.push({
      id: screen.id,
      title: screen.customTitle ?? screen.name,
      code: screen.code,
      path: screen.route,
      icon: screen.customIcon ?? screen.icon,
      module: screen.module,
      scope: '',
      items: active
        .filter((child) => child.parentId === screen.id)
        .map(toNavItem)
        .sort(bySort),
    });
  }

  // Re-sort groups the way the backend does (effective order, then code) so restored groups
  // land in their usual place; groups the subsidiary list doesn't know keep their order, last.
  const rank = new Map(screens.map((s) => [s.id, effectiveOrder(s)]));
  const sorted = groups
    .map((group, index) => ({ group, index }))
    .sort((a, b) => {
      const ra = rank.get(a.group.id) ?? Number.MAX_SAFE_INTEGER;
      const rb = rank.get(b.group.id) ?? Number.MAX_SAFE_INTEGER;
      if (ra !== rb) return ra - rb;
      if (ra === Number.MAX_SAFE_INTEGER) return a.index - b.index;
      return a.group.code.localeCompare(b.group.code);
    })
    .map(({ group }) => group);
  return { groups: sorted };
}

/**
 * Fetches the navigation menu from GET /screens/navigation.
 * Query key includes tenantId, userId, and isImpersonating to auto-bust
 * the cache on tenant switch, impersonation start/stop, and user change.
 * For super admins the tree also carries the subsidiary-disabled screens (see
 * `withSubsidiaryDisabledScreens`); the merge happens here, not in the cache, because
 * post-login redirect shares the raw navigation query key.
 */
export function useNavigation() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const tenantId = useAuthStore((s) => s.user?.activeTenantId ?? '');
  const userId = useAuthStore((s) => s.user?.id ?? '');
  const isImpersonating = useAuthStore((s) => s.isImpersonating);
  const isSuperAdmin = useAuthStore((s) => s.user?.isSuperAdmin ?? false);

  const query = useQuery({
    queryKey: queryKeys.navigation.menu(tenantId, userId, isImpersonating),
    queryFn: fetchNavigation,
    enabled: isAuthenticated && !!tenantId,
    staleTime: ONE_MINUTE,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: 1,
  });

  // Same key as the Navigation Access page, so a toggle there refreshes the merge too.
  const subsidiaryScreens = useQuery({
    queryKey: queryKeys.screens.subsidiary(tenantId),
    queryFn: () => getSubsidiaryScreens(tenantId),
    enabled: isAuthenticated && !!tenantId && isSuperAdmin,
    staleTime: FIVE_MINUTES,
    retry: 1,
  });

  const data =
    isSuperAdmin && query.data && subsidiaryScreens.data
      ? withSubsidiaryDisabledScreens(query.data, subsidiaryScreens.data)
      : query.data;

  return { ...query, data };
}
